import { useEffect, useState } from "react";
import type { SelectOption } from "@/components/ui/select-dropdown";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { AgentSummary } from "@/services/api/agentic-v2/types";

export interface GuardiansFilterOptions {
  missionTemplates: SelectOption<string>[];
  agents: SelectOption<string>[];
  catalogues: SelectOption<string>[];
  catalogueKeyById: Record<string, string>;
}

/** One option per agent display name (multiple DB rows can share a name). */
function uniqueAgentOptions(rows: AgentSummary[]): SelectOption<string>[] {
  const byName = new Map<string, AgentSummary>();
  for (const row of rows) {
    const existing = byName.get(row.name);
    if (!existing || row._id < existing._id) {
      byName.set(row.name, row);
    }
  }
  return [...byName.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((a) => ({ value: a._id, label: a.name }));
}

/** Mission template, agent, and catalogue dropdown options for Guardians filters. */
export function useGuardiansFilterOptions(): GuardiansFilterOptions {
  const [missionTemplates, setMissionTemplates] = useState<SelectOption<string>[]>([
    { value: "", label: "All missions" },
  ]);
  const [agents, setAgents] = useState<SelectOption<string>[]>([
    { value: "", label: "All agents" },
  ]);
  const [catalogues, setCatalogues] = useState<SelectOption<string>[]>([
    { value: "", label: "All catalogue" },
  ]);
  const [catalogueKeyById, setCatalogueKeyById] = useState<Record<string, string>>({});

  useEffect(() => {
    let mounted = true;
    Promise.all([
      AgenticV2API.listMissionTemplates(),
      AgenticV2API.listAgents(),
      AgenticV2API.listTaskCatalogue({ limit: 200 }),
    ])
      .then(([templates, agentRows, catRows]) => {
        if (!mounted) return;
        setMissionTemplates([
          { value: "", label: "All missions" },
          ...templates.map((t) => ({
            value: t._id,
            label: t.name,
          })),
        ]);
        setAgents([{ value: "", label: "All agents" }, ...uniqueAgentOptions(agentRows)]);
        setCatalogues([
          { value: "", label: "All catalogue" },
          ...catRows.map((c) => ({
            value: c._id,
            label: c.display_name || c.task_type,
          })),
        ]);
        const keyById: Record<string, string> = {};
        for (const c of catRows) {
          keyById[c._id] = c.key;
        }
        setCatalogueKeyById(keyById);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return { missionTemplates, agents, catalogues, catalogueKeyById };
}
